import { getSlotId, groupByTimeSlots } from './utils';
import { WorkerManager } from './worker-manager';
import type { DownloadResult } from './types';
import { SessionDataItem } from '../../types/session';
import { sessionDataManager } from './session-db';
import { createManifest, idsNeedingValidation, planSync, type SessionManifest } from './manifest';
import { DOWNLOAD_RETRY_DELAYS } from './constants';
import client_settings from '../client_settings';

const DEBUG = false;
const logger = (...content) => DEBUG && console.log(content);

export interface PackFailure {
  athleteSessionId: string;
  error: string;
  attempts: number;
}

export interface PackResult {
  /** every expected athlete has valid data */
  ok: boolean;
  done: string[];
  failed: PackFailure[];
}

export interface PackCallbacks {
  /** called for each athlete whose data is available (downloaded now or already valid) */
  onProgress?: (id: string) => void;
  /** called for each athlete that could not be downloaded after all retries */
  onFailed?: (id: string, error: string) => void;
  onCompleted?: () => void;
}

/**
 * Downloads, validates (with retries, inside the worker) and stores the data of one athlete session.
 * Throws if the data could not be obtained.
 */
const packUrlData = async (sessionId: string, athSessionId: string, url: string): Promise<DownloadResult> => {
  const workerManager = new WorkerManager();

  try {
    console.log('🚀 Avvio download e decodifica ottimizzata...');
    const now = performance.now();
    const result = await workerManager.downloadFromUrlAndDecodePack(
      sessionId,
      athSessionId,
      url,
      { format: 'binary' },
      (progress) => {
        console.log(`📊 ${progress.message} (${progress.progress.toFixed(1)}%)`);
      }
    );

    const time = performance.now() - now;

    console.log(`✅ Completato in ${time} ms`, result);
    return result;
  } catch (error) {
    console.error('❌ Errore:', error);
    throw error;
  } finally {
    workerManager.terminate();
  }
}

/**
 * Syncs the session data with the local cache: reuses valid stored blocks and downloads only the
 * missing / failed / broken ones. The progress is persisted in the session manifest, so an interrupted
 * download resumes where it stopped.
 *
 * @param athSessions - list returned by `enqueue_viewer_data` (athlete session id + url).
 * @param sessionId - the session identifier.
 * @param callbacks - onProgress (success only), onFailed, onCompleted.
 * @returns per-athlete result: `done` ids and `failed` entries.
 */
const packData = async (
  athSessions: SessionDataItem[],
  sessionId: string,
  callbacks: PackCallbacks = {},
): Promise<PackResult> => {
  const { onProgress, onFailed, onCompleted } = callbacks;
  logger('packData', athSessions, sessionId);

  const items = athSessions.filter((a) => !!a.athleteSessionId);
  const urls: Record<string, string> = {};
  items.forEach((a) => { urls[String(a.athleteSessionId)] = a.url; });
  const expected = items.map((a) => String(a.athleteSessionId));

  const done: string[] = [];
  const failed: PackFailure[] = [];

  try {
    const db = sessionDataManager;
    const prevManifest = await db.getManifest(sessionId);
    const storedIds = await db.getStoredAthleteIds(sessionId);

    // blocks without a manifest entry (cache from before the manifest) are validated, not blindly trusted
    const toValidate = idsNeedingValidation(expected, prevManifest, storedIds);
    const { validations, bytes } = toValidate.length > 0
      ? await db.validateStoredBlocks(sessionId, toValidate)
      : { validations: {}, bytes: {} };

    const plan = planSync(expected, prevManifest, storedIds, validations, bytes);

    if (plan.invalidateAll) {
      console.warn(`Sessione ${sessionId}: blocchi vuoti o non decodificabili, riscarico tutta la sessione`);
      await db.deleteSession(sessionId);
    } else {
      for (const id of plan.toDeleteBlocks) {
        await db.deleteStoredBlock(sessionId, id);
      }
    }

    const manifest: SessionManifest = {
      ...createManifest(sessionId, expected, plan.autoRedownloads),
      done: { ...plan.done },
      // failures of previous runs are retried now: they are re-recorded below if they fail again
      failed: {},
    };
    await db.saveManifest(manifest);

    // already valid data: no download
    Object.keys(plan.done).forEach((id) => {
      done.push(id);
      onProgress?.(id);
    });

    for (const id of plan.toDownload) {
      logger('packData - ', sessionId, id);

      try {
        const result = await packUrlData(sessionId, id, urls[id]);

        manifest.done[id] = {
          bytes: result.fileSize,
          records: result.records,
          firstTime: result.firstTime,
          lastTime: result.lastTime,
        };
        await db.saveManifest(manifest);
        done.push(id);
        onProgress?.(id);
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        const attempts = DOWNLOAD_RETRY_DELAYS.length + 1;

        manifest.failed[id] = { attempts, error, at: Date.now() };
        await db.saveManifest(manifest).catch((err) => console.error('Errore salvataggio manifest', err));
        failed.push({ athleteSessionId: id, error, attempts });
        onFailed?.(id, error);
      }
    }
  } catch (e) {
    // errore inatteso (es. IndexedDB): nessun dato affidabile
    const error = e instanceof Error ? e.message : String(e);
    console.error('❌ Errore packData', e);
    expected.filter((id) => !done.includes(id) && !failed.some((f) => f.athleteSessionId === id)).forEach((id) => {
      failed.push({ athleteSessionId: id, error, attempts: 0 });
      onFailed?.(id, error);
    });
  }

  onCompleted?.();

  return { ok: failed.length === 0 && done.length === expected.length, done, failed };
}

/**
 * Verifica la disponibilità dei dati del visualizzatore associati a una sessione specificata.
 *
 * Questa funzione asincrona effettua una richiesta al backend per verificare se
 * i dati del visualizzatore sono disponibili per un determinato ID sessione. Richiede un token
 * di autenticazione per eseguire la richiesta.
 *
 * @param sessionId - L'identificatore della sessione per controllare i dati del visualizzatore.
 * @returns Una promise che si risolve in un oggetto contenente il codice di stato HTTP
 *          e un booleano che indica se i dati del visualizzatore sono disponibili.
 * @throws Genera un errore se la richiesta HTTP al backend fallisce o restituisce un
 *         codice di stato non-OK.
 */
const checkSessionDataAvailable = async (sessionId: string): Promise<{
  status: number,
  available: boolean,
  processing: boolean,
}> => {
  const token = localStorage.getItem('exelio_token');

  if (!token) {
    throw new Error(`Anomalia - No Token`); // @todo logout?
  }

  let resp: Response;

  try {
    resp = await fetch(
      `${client_settings.public.gpexeBackendUrl}rest/v2/session/team/${sessionId}/check_viewer_data/`,
      {
        headers: {
          'Authorization': `JWT ${token}`,
        }
      }
    );
  } catch (e) {
    console.error('Errore nella verifica dati disponibili', e);
    throw new Error(`HTTP error!`);
  }

  const respData = await resp.json();

  return {
    status: resp.status,
    available: respData.available,
    processing: respData.processing,
  }
}

/**
 * Asynchronously loads session data for a given session ID and authentication token.
 *
 * This function fetches viewer data for a specific session and ensures
 * proper authentication using a provided JWT token. If the request is
 * successful, it returns the HTTP status and a list of uploaded files
 * associated with the session. In case of an error, it throws an exception
 * indicating the HTTP error status.
 *
 * @param sessionId The session identifier for which the data will be loaded.
 * @param token The JWT authentication token used to access session data.
 * @returns An object containing the HTTP status and session-related data, such as files uploaded.
 * @throws An error if the HTTP response status is not successful.
 */
const loadSessionData = async (sessionId: string, token: string): Promise<{
  status: number,
  athSessions: SessionDataItem[],
}> => {
  const resp = await fetch(
    `${client_settings.public.gpexeBackendUrl}rest/v2/session/team/${sessionId}/enqueue_viewer_data/`,
    {
      headers: {
        'Authorization': `JWT ${token}`,
      }
    }
  );

  if (!resp.ok) {
    throw new Error(`HTTP error! status: ${resp.status}`);
  }

  const respData = await resp.json();

  return {
    status: resp.status,
    athSessions: respData.files_uploaded || respData,
  }
}

/**
 * Asynchronously loads and processes data for a specific session.
 *
 * This function creates a `WorkerManager` instance to handle the data loading process.
 * It performs reading and decoding of the data block associated with the given `sessionId` and `timestamp`.
 * After successfully processing the data, it groups the output into time slots and returns the result.
 * The worker manager is terminated in a `finally` block to ensure cleanup of resources.
 *
 * @param sessionId - Identifier for the session whose data needs to be loaded.
 * @param timestamp - The timestamp indicating the specific time block to load data for.
 * @returns Processed output grouped by time slots.
 * @throws Any error occurring during the data loading process is logged.
 */
const loadData = async (sessionId: string, timestamp: number) => {
  const workerManager = new WorkerManager();

  try {
    console.log('🚀 Lettura e decodifica...', timestamp);
    const now = performance.now();
    const out = await workerManager.loadDataBlock(
      sessionId,
      timestamp,
    );

    // filtrare per timestamp

    const data = groupByTimeSlots(out.output);

    const time = performance.now() - now;

    logger(`✅ Completato in ${time} ms`);
    return data;
  } catch (error) {
    console.error('❌ Errore:', error);
  } finally {
    logger('terminate')
    workerManager.terminate();
  }
}

/**
 * Retrieves player data based on the provided timestamp and state object.
 *
 * This function determines the slot identifier associated with the given
 * timestamp and checks if the state object contains data for the respective slot.
 *
 * @param timestamp The specific timestamp used to retrieve player data.
 * @param state The state object containing player data organized by slot and timestamp.
 */
const getPlayersData = (timestamp: number, state: any)=> {
  const slotId = getSlotId(timestamp);
debugger;
  console.log('getPlayersData', timestamp, slotId, state)

  if (!state || !state[slotId]) {
    console.log('no data')
  } else {
    console.log('dati trovati', state[slotId], state[slotId][timestamp]);
    console.log('dati players', Object.keys(state[slotId]))
    console.log('dati path', Object.keys(state[slotId]).map((player) => state[slotId][player][timestamp]))
    console.log('debug timestamps', Object.keys(state[slotId]).map((player) => Object.keys(state[slotId][player]).slice(0, 10)))
  }
}

export {
  checkSessionDataAvailable,
  loadData,
  loadSessionData,
  getPlayersData,
  packData,
}
